/*
Search modal overlay component.

Replaces <dialog> with a div/CSS overlay to avoid z-index clashes with third-party
apps (e.g. wishlist). Keeps the same showDialog/closeDialog API for existing
on:click="#search-modal/showDialog" and on:click="#search-modal/closeDialog" handlers.
*/

import { Component } from '@theme/component';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Custom element for the search modal using a div overlay (no <dialog>).
 * Exposes showDialog() and closeDialog() for theme event delegation.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overlay – The backdrop overlay element.
 * @property {HTMLElement} content – The modal content panel.
 *
 * @extends Component<Refs>
 */
export class SearchModalComponent extends Component {
  static get tagName() {
    return 'search-modal-component';
  }

  requiredRefs = ['overlay', 'content'];

  /** @type {HTMLElement | null} Element that had focus before the modal opened */
  #previouslyFocused = null;

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (isClickedOutside(event, this.refs.content)) {
      this.closeDialog();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  #isOpen = false;

  connectedCallback() {
    super.connectedCallback();
    // Hidden from screen readers by default
    this.setAttribute('aria-hidden', 'true');
  }

  showDialog() {
    const { content } = this.refs;
    if (this.#isOpen) return;

    this.#isOpen = true;
    this.#previouslyFocused = /** @type {HTMLElement} */ (document.activeElement);
    this.removeAttribute('aria-hidden');
    document.documentElement.setAttribute('scroll-lock', '');

    requestAnimationFrame(() => {
      this.classList.add('search-modal--open');
      content.classList.add('search-modal__content--open');

      // Focus the search input for keyboard and screen reader users
      const input = this.querySelector('input[type="search"], input[type="text"], input');
      input?.focus();
    });

    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const { content } = this.refs;
    if (!this.#isOpen) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    content.classList.remove('search-modal__content--open');
    content.classList.add('search-modal__content--closing');

    await onAnimationEnd(content, undefined, { subtree: false });

    this.classList.remove('search-modal--open');
    content.classList.remove('search-modal__content--closing');
    this.setAttribute('aria-hidden', 'true');
    this.#isOpen = false;
    document.documentElement.removeAttribute('scroll-lock');

    // Return focus to the element that triggered the modal
    this.#previouslyFocused?.focus();
    this.#previouslyFocused = null;
  };
}

if (!customElements.get(SearchModalComponent.tagName)) {
  customElements.define(SearchModalComponent.tagName, SearchModalComponent);
}
